@let level = store.level();
@let summary = store.summary();

<div class="usage-anchor" clickOutside [clickOutsideEnabled]="open()" (clickOutside)="close()">
    <button
        type="button"
        class="usage-pill"
        [class.warn]="level === 'warn'"
        [class.critical]="level === 'critical' || level === 'exhausted'"
        [class.active]="open()"
        [attr.aria-expanded]="open()"
        [attr.aria-label]="'document.edit.usageLimits.title' | translate"
        [raEditTooltip]="open() ? null : { text: ('document.edit.usageLimits.pillTooltip' | translate), position: 'bottom' }"
        (click)="toggle()">
        @if (summary) {
            <ra-stacked-bar
                class="usage-pill__bar"
                [segments]="segments()"
                [total]="store.total()"
                [height]="6"
                [ariaLabel]="'document.edit.usageLimits.pillTooltip' | translate" />
            <span class="usage-pill__text">{{ store.usedPercent() }}%</span>
        } @else if (store.error()) {
            <ra-icon icon="chart" varColor="currentColor" [size]="16" />
        } @else {
            <ra-skeleton width="3rem" [height]="6" [radius]="999" />
        }
    </button>

    @if (open()) {
        <ra-popover [width]="340" [ariaLabel]="'document.edit.usageLimits.title' | translate">
            <header class="usage-pop__head">
                <strong>{{ 'document.edit.usageLimits.title' | translate }}</strong>
                <button type="button" class="usage-pop__link" (click)="goToUsage()">
                    {{ 'document.edit.usageLimits.viewUsage' | translate }}
                    <ra-icon icon="arrow-right" varColor="currentColor" [size]="14" />
                </button>
            </header>

            @if (summary) {
                <!-- Créditos do período: barra por categoria, como na tela de Consumo. -->
                <section class="usage-pop__block">
                    <div class="usage-pop__row">
                        <span class="usage-pop__label">{{ 'document.edit.usageLimits.periodCredits' | translate }}</span>
                        <span class="usage-pop__value">
                            {{ 'document.edit.usageLimits.usedOf' | translate: { used: UsageHumanize.formatTokens(store.used()), total: UsageHumanize.formatTokens(store.total()) } }}
                            <strong [class.warn]="level === 'warn'" [class.critical]="level === 'critical' || level === 'exhausted'">
                                {{ store.usedPercent() }}%
                            </strong>
                        </span>
                    </div>
                    <ra-stacked-bar [segments]="segments()" [total]="store.total()" [height]="8" />

                    @if (store.categories().length) {
                        <ul class="usage-pop__legend">
                            @for (category of store.categories(); track category.category) {
                                <li>
                                    <span class="usage-pop__dot" [style.background-color]="'var(--' + UsageHumanize.categoryColor(category.category) + ')'"></span>
                                    {{ UsageHumanize.categoryLabelKey(category.category) | translate }}
                                    <span class="usage-pop__muted">{{ UsageHumanize.formatTokens(category.sumTokens) }}</span>
                                </li>
                            }
                        </ul>
                    } @else {
                        <p class="usage-pop__muted">{{ 'document.edit.usageLimits.noUsage' | translate }}</p>
                    }

                    <p class="usage-pop__meta">
                        <ra-icon icon="calendar" varColor="contentTertiary" [size]="14" />
                        @let days = store.daysToRenew();
                        @if (days <= 0) {
                            {{ 'document.edit.usageLimits.renewsToday' | translate }}
                        } @else if (days === 1) {
                            {{ 'document.edit.usageLimits.renewsTomorrow' | translate }}
                        } @else {
                            {{ 'document.edit.usageLimits.renewsInDays' | translate: { days } }}
                        }
                        @if (renewDateText()) {
                            <span class="usage-pop__muted">· {{ renewDateText() }}</span>
                        }
                    </p>
                </section>

                <!-- O que ESTA funcionalidade gastou no período. -->
                @if (store.aiEdit(); as aiEdit) {
                    <section class="usage-pop__block usage-pop__block--ai">
                        <ra-icon icon="magicpen" varColor="accentGreen" [size]="16" />
                        <span class="usage-pop__ai-text">
                            <span class="usage-pop__label">{{ 'document.edit.usageLimits.aiEditTitle' | translate }}</span>
                            <span class="usage-pop__muted">
                                @if (aiEdit.count === 1) {
                                    {{ 'document.edit.usageLimits.aiEditOne' | translate: { credits: UsageHumanize.formatTokens(aiEdit.credits) } }}
                                } @else {
                                    {{ 'document.edit.usageLimits.aiEditMany' | translate: { count: aiEdit.count, credits: UsageHumanize.formatTokens(aiEdit.credits) } }}
                                }
                            </span>
                        </span>
                    </section>
                }

                <!-- Saldo: o número cheio, porque é ele que precisa se mover a cada gasto. -->
                <section class="usage-pop__balance" [class.warn]="level === 'warn'" [class.critical]="level === 'critical'" [class.exhausted]="level === 'exhausted'">
                    @if (level === 'exhausted') {
                        <p>{{ 'document.edit.usageLimits.exhausted' | translate }}</p>
                        <button type="button" class="usage-pop__button" (click)="goToPlans()">
                            {{ 'document.edit.usageLimits.viewPlans' | translate }}
                        </button>
                    } @else {
                        <p>
                            <strong>{{ 'document.edit.usageLimits.available' | translate: { credits: UsageHumanize.formatTokensExact(store.available()) } }}</strong>
                        </p>
                        @if (level === 'critical') {
                            <p class="usage-pop__hint">{{ 'document.edit.usageLimits.almostOut' | translate }}</p>
                        }
                    }
                </section>
            } @else if (store.error()) {
                <section class="usage-pop__block usage-pop__error">
                    <p>{{ 'document.edit.usageLimits.loadError' | translate }}</p>
                    <button type="button" class="usage-pop__button" (click)="retry()">
                        {{ 'common.retry' | translate }}
                    </button>
                </section>
            } @else {
                <section class="usage-pop__block" aria-busy="true">
                    <ra-skeleton width="60%" [height]="12" />
                    <ra-skeleton width="100%" [height]="8" [radius]="999" />
                    <ra-skeleton width="45%" [height]="10" />
                </section>
            }
        </ra-popover>
    }
</div>
